import PropTypes from 'prop-types';
import { Modal, Button } from './common';
import {
  ExclamationTriangleIcon,
  SparklesIcon,
  DocumentTextIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';

/**
 * Modal shown when domain mismatch is detected between resume and job
 * Gives user choice: Career Pivot Strategy vs Standard Tailoring
 */
const DomainMismatchModal = ({
  isOpen,
  onClose,
  domainAnalysis,
  onProceedStandard,
  onProceedCareerPivot,
  jobTitle,
  company,
  loading = false
}) => {
  if (!domainAnalysis) return null;

  const {
    matchScore,
    matchLevel,
    explanation,
    transferableSkills = [],
    criticalGaps = [],
    resumeDomain = {},
    jobDomain = {}
  } = domainAnalysis;

  // Color based on match level
  const getScoreColor = () => {
    if (matchLevel === 'High') return 'text-green-600';
    if (matchLevel === 'Medium') return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreBgColor = () => {
    if (matchLevel === 'High') return 'bg-green-100';
    if (matchLevel === 'Medium') return 'bg-yellow-100';
    return 'bg-red-100';
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Domain Mismatch Detected"
      size="lg"
    >
      <div className="space-y-6">
        {/* Warning Banner */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <ExclamationTriangleIcon className="h-6 w-6 text-amber-500 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-semibold text-amber-800">
                Significant Career Pivot Detected
              </h4>
              <p className="text-sm text-amber-700 mt-1">{explanation}</p>
            </div>
          </div>
        </div>

        {/* Domain Comparison */}
        <div className="grid grid-cols-2 gap-4">
          <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
            <h5 className="font-medium text-gray-700 mb-2 text-sm">Your Background</h5>
            <p className="text-sm font-semibold text-gray-900">
              {resumeDomain.industry || 'Unknown'}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              {resumeDomain.roleFunction || 'Unknown'}
            </p>
          </div>
          <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
            <h5 className="font-medium text-gray-700 mb-2 text-sm">Target Role</h5>
            <p className="text-sm font-semibold text-gray-900">
              {jobDomain.industry || 'Unknown'}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              {jobDomain.roleFunction || 'Unknown'}
            </p>
          </div>
        </div>

        {/* Match Score Visual */}
        <div className="text-center py-4">
          <div className={`inline-flex items-center gap-3 px-6 py-3 rounded-full ${getScoreBgColor()}`}>
            <span className={`text-4xl font-bold ${getScoreColor()}`}>
              {matchScore}%
            </span>
            <span className="text-gray-600 text-sm">Domain Match</span>
          </div>
        </div>

        {/* Transferable Skills */}
        {transferableSkills.length > 0 && (
          <div>
            <h5 className="font-medium text-gray-700 mb-2 text-sm">
              Transferable Skills
            </h5>
            <div className="flex flex-wrap gap-2">
              {transferableSkills.map((skill, i) => (
                <span
                  key={i}
                  className="px-2 py-1 bg-green-100 text-green-800 rounded text-sm"
                >
                  {skill}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Critical Gaps */}
        {criticalGaps.length > 0 && (
          <div>
            <h5 className="font-medium text-gray-700 mb-2 text-sm">
              Critical Gaps for This Role
            </h5>
            <div className="flex flex-wrap gap-2">
              {criticalGaps.map((gap, i) => (
                <span
                  key={i}
                  className="px-2 py-1 bg-red-100 text-red-800 rounded text-sm"
                >
                  {gap}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Divider */}
        <div className="border-t border-gray-200" />

        {/* Action Explanation */}
        <div className="text-sm text-gray-600">
          <p className="font-medium text-gray-700 mb-2">Choose your approach:</p>
          <ul className="space-y-2 ml-4">
            <li className="flex items-start gap-2">
              <SparklesIcon className="h-4 w-4 text-primary-600 mt-0.5 flex-shrink-0" />
              <span>
                <strong>Career Pivot Strategy:</strong> Honestly highlights transferable skills
                without fabricating domain expertise. Better for networking and direct outreach.
              </span>
            </li>
            <li className="flex items-start gap-2">
              <DocumentTextIcon className="h-4 w-4 text-gray-500 mt-0.5 flex-shrink-0" />
              <span>
                <strong>Tailor Anyway:</strong> Standard keyword optimization. May produce
                a forced fit with misleading results.
              </span>
            </li>
          </ul>
        </div>

        {/* Action Buttons */}
        <div className="space-y-3 pt-2">
          <Button
            onClick={onProceedCareerPivot}
            loading={loading}
            disabled={loading}
            className="w-full justify-center"
          >
            <SparklesIcon className="h-5 w-5 mr-2" />
            <div className="text-left">
              <span className="font-semibold">Career Pivot Strategy</span>
              <span className="block text-xs text-primary-200 font-normal">
                Recommended for significant career changes
              </span>
            </div>
          </Button>

          <Button
            variant="secondary"
            onClick={onProceedStandard}
            loading={loading}
            disabled={loading}
            className="w-full justify-center"
          >
            <DocumentTextIcon className="h-5 w-5 mr-2" />
            <div className="text-left">
              <span className="font-semibold">Tailor Anyway</span>
              <span className="block text-xs text-gray-500 font-normal">
                Standard tailoring (may produce forced fit)
              </span>
            </div>
          </Button>

          <button
            onClick={onClose}
            disabled={loading}
            className="w-full text-center text-sm text-gray-500 hover:text-gray-700 py-2 disabled:opacity-50"
          >
            Cancel and review job requirements
          </button>
        </div>

        {/* Job context footer */}
        <div className="text-xs text-gray-400 text-center pt-2 border-t border-gray-100">
          Applying for: {jobTitle} at {company}
        </div>
      </div>
    </Modal>
  );
};

DomainMismatchModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  domainAnalysis: PropTypes.shape({
    matchScore: PropTypes.number,
    matchLevel: PropTypes.string,
    explanation: PropTypes.string,
    transferableSkills: PropTypes.arrayOf(PropTypes.string),
    criticalGaps: PropTypes.arrayOf(PropTypes.string),
    resumeDomain: PropTypes.shape({
      industry: PropTypes.string,
      roleFunction: PropTypes.string
    }),
    jobDomain: PropTypes.shape({
      industry: PropTypes.string,
      roleFunction: PropTypes.string
    })
  }),
  onProceedStandard: PropTypes.func.isRequired,
  onProceedCareerPivot: PropTypes.func.isRequired,
  jobTitle: PropTypes.string,
  company: PropTypes.string,
  loading: PropTypes.bool
};

export default DomainMismatchModal;
